import { compose, withProps, withStateHandlers, withHandlers } from 'recompose'
import { cloneDeep, forEach, forIn, get, merge, isEqual, isNil, throttle, remove, isEmpty } from 'lodash'

import { setWrapDisplayName } from 'hoc/setWrapDisplayName'
import { withComponentDidUpdate } from 'hoc/withComponentDidUpdate'

export const initialFormMeta = { isTouched: false, isValid: false, errors: [], touchedFields: [] }

export const updateRequiredFields = ({ formData, source, index }) =>
  forEach(formData, field => {
    if (Array.isArray(field)) {
      field.forEach((nestedFormData, fieldIndex) => {
        updateRequiredFields({ formData: nestedFormData, source, index: fieldIndex })
      })

      return
    }

    if (!isFormField(field)) {
      updateRequiredFields({ formData: field, source, index })

      return
    }

    if (field.required) {
      field.isRequired = field.required(source, index)
    }
  })

const isFormField = field => get(field, 'isFormField', false)

const setResponseToField = ({ formData, response }) => {
  forEach(formData, field => {
    if (field.responseValidator && field.responseErrorCodes && response) {
      const responseError = field.responseValidator(field.responseErrorCodes, response)

      if (responseError) {
        field.responseError = responseError
        field.meta.isTouched = false
      }
    }
  })
}

export const validateFields = ({ formData, source, index }) =>
  forEach(formData, field => {
    if (Array.isArray(field)) {
      field.forEach((nestedFormData, fieldIndex) => {
        validateFields({ formData: nestedFormData, source, index: fieldIndex })
      })

      return
    }

    if (!isFormField(field)) {
      validateFields({ formData: field, source, index })

      return
    }

    if (field.validator && field.meta.isTouched && !field.responseError) {
      field.error = field.validator(source, field, index)
    }

    // check BE errors
    if (field.responseError) {
      if (field.meta.isTouched) {
        field.error = field.validator ? field.validator(source, field) : ''
        field.responseError = ''
      } else {
        field.error = field.responseError
      }
    }
  })

export const validateFormMeta = ({ formData, formMeta, source, isNested = false, index }) => {
  if (!isNested) {
    formMeta.errors = []
    formMeta.touchedFields = []
  }

  forEach(formData, formItem => {
    if (Array.isArray(formItem)) {
      formItem.forEach((nestedFormData, index) => {
        validateFormMeta({ formData: nestedFormData, formMeta, source, isNested: true, index })
      })

      return
    }

    if (!isFormField(formItem)) {
      validateFormMeta({ formData: formItem, formMeta, source, isNested: true })

      return
    }

    if (
      !Array.isArray(formItem) &&
      formItem.validator &&
      (formItem.meta.isTouched || formItem.isRequired) &&
      !formItem.responseError
    ) {
      const error = formItem.validator(source, formItem, index)

      error && formMeta.errors.push(error)
    }

    // check BE errors
    if (formItem.responseError && !formItem.meta.isTouched) {
      formMeta.errors.push(formItem.responseError)
    }

    if (formItem.meta.isTouched) {
      formMeta.touchedFields.push(formItem)
    }
  })

  formMeta.isValid = !formMeta.errors.length
}

const initializeField = ({ field, key, initialFormValues, fieldValueGetter }) => {
  const defaultFieldProps = {
    id: '',
    value: undefined,
    meta: { isTouched: false },
    error: '',
    isRequired: false,
    isFormField: true,
  }

  let resultField = merge({}, defaultFieldProps, field)

  if (!resultField.id) {
    resultField.id = key
  }

  if (!resultField.name) {
    resultField.name = key
  }

  if (initialFormValues) {
    if (field.primitive) {
      resultField.value = initialFormValues
    } else if (initialFormValues && !isNil(initialFormValues[key])) {
      resultField.value = fieldValueGetter ? fieldValueGetter(initialFormValues[key]) : initialFormValues[key]
    }
  }

  return resultField
}

export const getInitialFormFields = ({ formConfig, initialFormValues, fieldValueGetter }) => {
  const config = cloneDeep(formConfig)

  forIn(config, (fieldContent, key) => {
    const initialValues = get(initialFormValues, key, null)

    if (fieldContent.fieldsArray) {
      config[key] = []

      if (initialValues && initialValues.length) {
        forEach(initialValues, value => {
          config[key].push(
            getInitialFormFields({
              formConfig: fieldContent.fieldsArray,
              initialFormValues: value,
              fieldValueGetter,
            }),
          )
        })
      } else {
        if (!isEmpty(fieldContent.fieldsArray)) {
          config[key].push(
            getInitialFormFields({
              formConfig: fieldContent.fieldsArray,
              initialFormValues: initialValues,
              fieldValueGetter,
            }),
          )
        }
      }

      return
    }

    if (fieldContent.fieldsMap) {
      config[key] = {
        ...getInitialFormFields({
          formConfig: fieldContent.fieldsMap,
          initialFormValues: initialValues,
          fieldValueGetter,
        }),
      }

      return
    }

    config[key] =
      !fieldContent.skipInitialize && initializeField({ field: fieldContent, key, initialFormValues, fieldValueGetter })
  })

  return config
}

const getInitialFormMeta = ({ formData, validatePrefilledForm }) => {
  const formMeta = { ...initialFormMeta }

  if (validatePrefilledForm) {
    validateFormMeta({ formData, formMeta, source: formData })
    formMeta.isTouched = true
  }

  return formMeta
}

const getFormPayload = formData => {
  let formPayload = {}

  forIn(formData, (formItem, key) => {
    if (Array.isArray(formItem)) {
      const fieldsArrayContent = []

      formItem.forEach(fieldsArrayData => {
        fieldsArrayContent.push(getFormPayload(fieldsArrayData))
      })

      formPayload[key] = fieldsArrayContent.filter(item => item !== undefined) // Note: should filter empty items.

      return
    }

    if (isFormField(formItem)) {
      if (formItem.primitive) {
        formPayload = get(formItem, 'value')
      } else {
        formPayload[key] = get(formItem, 'value')
      }
    } else {
      formPayload[key] = getFormPayload(formItem)
    }
  })

  return formPayload
}

/**
 * Manages complex form state
 *
 * `getOwnProps` is expected to return:
 * - initialFormValues - Optional object with initial field values
 * Example: { firstName: 'First Name' }
 *
 * - formConfig - Optional object with field validation config
 * Example: { firstName: { validator: validateName, isRequired: true } }
 *
 * - fieldValueGetter - Optional function to get value from any field
 * Example: field => get(field, 'value')
 *
 * - dataChangeCallback - Optional function that will receive `{ formData, formMeta }` param when any field changes
 *
 * - validatePrefilledForm - Optional boolean. If true, executes form validation / fires callbacks on creation
 */
export const withForm = getOwnProps => BaseComponent =>
  compose(
    setWrapDisplayName(BaseComponent, 'withForm'),
    withProps(props => ({ ...getOwnProps(props) })),
    withStateHandlers(
      props => {
        const { validatePrefilledForm, dataChangeCallback = () => {} } = props
        const formData = getInitialFormFields(props)

        updateRequiredFields({ formData, source: formData })

        const formMeta = getInitialFormMeta({ formData, validatePrefilledForm })

        if (validatePrefilledForm) {
          dataChangeCallback({ formData, formMeta })
        }

        return { formData, formMeta }
      },
      {
        setFormData: () => formData => ({ formData }),
        setFormMeta: () => formMeta => ({ formMeta }),
        handleAddFields:
          ({ formData, formMeta }) =>
          ({ fields, path, initialFormValues, dataChangeCallback }) => {
            const updatedFormData = cloneDeep(formData)
            const updatedFormMeta = cloneDeep(formMeta)
            const newFields = getInitialFormFields({ formConfig: fields, initialFormValues })
            const placeToInsert = path ? get(updatedFormData, path) : updatedFormData

            if (Array.isArray(placeToInsert)) {
              placeToInsert.push(newFields)
            } else {
              merge(placeToInsert, newFields)
            }

            updateRequiredFields({ formData: updatedFormData, source: updatedFormData })
            validateFields({ formData: updatedFormData, source: updatedFormData })
            validateFormMeta({ formData: updatedFormData, formMeta: updatedFormMeta, source: updatedFormData })

            dataChangeCallback && dataChangeCallback({ formData: updatedFormData, formMeta: updatedFormMeta })

            return {
              formData: updatedFormData,
              formMeta: updatedFormMeta,
            }
          },
        handleRemoveFields:
          ({ formData, formMeta }, { dataChangeCallback }) =>
          ({ path, index }) => {
            const updatedFormData = cloneDeep(formData)
            const updatedFormMeta = cloneDeep(formMeta)

            remove(updatedFormData[path], (item, itemIndex) => index === itemIndex)

            updateRequiredFields({ formData: updatedFormData, source: updatedFormData })
            validateFields({ formData: updatedFormData, source: updatedFormData })
            validateFormMeta({ formData: updatedFormData, formMeta: updatedFormMeta, source: updatedFormData })

            dataChangeCallback && dataChangeCallback({ formData: updatedFormData, formMeta: updatedFormMeta })

            return {
              formData: updatedFormData,
              formMeta: updatedFormMeta,
            }
          },
        handleSetFieldResponseError:
          ({ formData, formMeta }, { dataChangeCallback }) =>
          (fieldName, errorText) => {
            const updatedFormData = cloneDeep(formData)
            const updatedFormMeta = cloneDeep(formMeta)
            const field = get(updatedFormData, fieldName)

            field.error = errorText
            field.responseError = errorText
            validateFormMeta({ formData: updatedFormData, formMeta: updatedFormMeta, source: updatedFormData })
            dataChangeCallback && dataChangeCallback({ formData: updatedFormData, formMeta: updatedFormMeta })

            return {
              formData: updatedFormData,
              formMeta: updatedFormMeta,
            }
          },
        handleFieldChange:
          ({ formData, formMeta }, { dataChangeCallback = () => {} }) =>
          (name, value) => {
            const updatedFormData = cloneDeep(formData)
            const updatedFormMeta = cloneDeep(formMeta)
            const field = get(updatedFormData, name)

            field.value = value

            if (!field.meta.isTouched) {
              field.meta.isTouched = true
            }

            if (!updatedFormMeta.isTouched) {
              updatedFormMeta.isTouched = true
            }

            updateRequiredFields({ formData: updatedFormData, source: updatedFormData })
            validateFields({ formData: updatedFormData, source: updatedFormData })
            validateFormMeta({ formData: updatedFormData, formMeta: updatedFormMeta, source: updatedFormData })

            dataChangeCallback({ formData: updatedFormData, formMeta: updatedFormMeta })

            return {
              formData: updatedFormData,
              formMeta: updatedFormMeta,
            }
          },
        handleFieldsChange: throttle(
          ({ formData, formMeta }, { dataChangeCallback = () => {} }) =>
            fields => {
              const updatedFormData = cloneDeep(formData)
              const updatedFormMeta = cloneDeep(formMeta)

              forEach(fields, (value, name) => {
                const field = updatedFormData[name]

                field.value = value

                if (!field.meta.isTouched) {
                  field.meta.isTouched = true
                }
              })

              if (!updatedFormMeta.isTouched) {
                updatedFormMeta.isTouched = true
              }

              updateRequiredFields({ formData: updatedFormData, source: updatedFormData })
              validateFields({ formData: updatedFormData, source: updatedFormData })
              validateFormMeta({ formData: updatedFormData, formMeta: updatedFormMeta, source: updatedFormData })

              dataChangeCallback({ formData: updatedFormData, formMeta: updatedFormMeta })

              return {
                formData: updatedFormData,
                formMeta: updatedFormMeta,
              }
            },
          200,
        ),
        handleFieldsUntouch:
          ({ formData, formMeta }, { dataChangeCallback = () => {} }) =>
          (fieldsNames, cleanValue) => {
            const updatedFormData = cloneDeep(formData)
            const updatedFormMeta = cloneDeep(formMeta)

            forEach(fieldsNames, name => {
              const field = updatedFormData[name]

              if (field.meta.isTouched) {
                field.meta.isTouched = false
                field.error = ''
              }

              if (cleanValue && field.value) {
                field.value = ''
              }
            })

            validateFormMeta({ formData: updatedFormData, formMeta: updatedFormMeta, source: updatedFormData })
            dataChangeCallback({ formData: updatedFormData, formMeta: updatedFormMeta })

            return {
              formData: updatedFormData,
              formMeta: updatedFormMeta,
            }
          },
        handleResponseErrorsCheck:
          ({ formData, formMeta }) =>
          response => {
            const updatedFormData = cloneDeep(formData)
            const updatedFormMeta = cloneDeep(formMeta)

            // set BE errors to formData
            setResponseToField({ formData: updatedFormData, response })
            validateFields({ formData: updatedFormData, source: updatedFormData })
            validateFormMeta({ formData: updatedFormData, formMeta: updatedFormMeta, source: updatedFormData })

            return {
              formData: updatedFormData,
              formMeta: updatedFormMeta,
            }
          },
        handleFormClear: (state, props) => () => ({
          formData: getInitialFormFields(props),
          formMeta: initialFormMeta,
        }),
        handleInitialValidation:
          ({ formMeta, formData }) =>
          () => {
            validateFormMeta({ formMeta, formData, source: formData })
            formMeta.isTouched = true

            return {
              formMeta,
            }
          },
        // NOTE: Mark all fields as touched and execute validation
        handleValidateAllFields:
          ({ formMeta, formData }, { dataChangeCallback = () => {} }) =>
          (onValidate = null) => {
            const updatedFormData = cloneDeep(formData)
            const updatedFormMeta = cloneDeep(formMeta)

            forEach(updatedFormData, field => {
              field.meta.isTouched = true
            })
            updatedFormMeta.isTouched = true

            validateFields({ formData: updatedFormData, source: updatedFormData })
            validateFormMeta({ formData: updatedFormData, formMeta: updatedFormMeta, source: updatedFormData })

            dataChangeCallback({ formData: updatedFormData, formMeta: updatedFormMeta })

            if (onValidate) {
              onValidate({ formData: updatedFormData, formMeta: updatedFormMeta })
            }

            return {
              formData: updatedFormData,
              formMeta: updatedFormMeta,
            }
          },
      },
    ),
    withHandlers({
      // NOTE: proxy method that allows to execute `handleFieldChange` in format `handleFieldChange(name)(value)`
      handleFieldChange:
        ({ handleFieldChange }) =>
        name =>
        value =>
          handleFieldChange(name, value),
      handleSetFieldResponseError:
        ({ handleSetFieldResponseError }) =>
        name =>
        errorText =>
          handleSetFieldResponseError(name, errorText),
      handleAddFields:
        ({ handleAddFields }) =>
        params =>
          handleAddFields(params),
      handleRemoveFields:
        ({ handleRemoveFields }) =>
        params =>
          handleRemoveFields(params),
      getFormPayload:
        ({ formData }) =>
        () =>
          getFormPayload(formData),
      // NOTE: proxy method that returns a promise when full validation is done.
      handleValidateAllFields:
        ({ handleValidateAllFields }) =>
        () =>
          new Promise(resolve => {
            handleValidateAllFields(resolve)
          }),
    }),
    // NOTE: update form when `initialFormValues` were changed
    withComponentDidUpdate((props, prevProps) => {
      if (!isEqual(props.initialFormValues, prevProps.initialFormValues)) {
        props.setFormData(getInitialFormFields(props))

        if (props.validatePrefilledForm) {
          props.handleInitialValidation()

          if (props.dataChangeCallback) {
            props.dataChangeCallback(props)
          }
        }
      }
    }),
  )(BaseComponent)
